import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import { Ionicons, Feather } from '@expo/vector-icons';
import getPlatformType from '../../helpers/helpers';
import Urls from '../../services/Urls';
import ajax from '../../helpers/ajaxHelper';
import { useRoute, useIsFocused, useNavigation } from '@react-navigation/native';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import MarkAsComplete from '../../components/markAsComplete/MarkAsComplete';
import Select from 'react-select';
import { showErrorToast } from '../tenants/tenantOnboarding/helpers';
import { COLORS, BORDER_RADIUS, SHADOWS, LAYOUT } from '../../constants/theme';
import { navigate } from '../../navigation/RootNavigation';
import { SetupSidebar, usePropertyRouteParams, PropertyHeaderBanner, PropertyStickyFooter } from './sharedComponents';

const PropertyAddress = ({ navigation: navProp }) => {
  const navigation = useNavigation();
  const route = useRoute();
  const routeParams = route?.params || {};
  const activePropertyId = routeParams.id;
  const focused = useIsFocused();
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [state, setState] = useState({
    postcode: '',
    houseName: '',
    address1: '',
    city: '',
    country: '',
    property_name: '',
    property_worth: '',
    mortgage_provider: '',
    id: '',
    hubita_id: '',
  });

  const [postcodes, setPostcodes] = useState([]);
  const [mortageProviders, setMP] = useState([]);

  const resetFormState = () => {
    setState({
      postcode: '',
      houseName: '',
      address1: '',
      city: '',
      country: '',
      property_name: '',
      property_worth: '',
      mortgage_provider: '',
      id: '',
      hubita_id: '',
    });
    setPostcodes([]);
    if (typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.removeItem('active_property_id');
        sessionStorage.removeItem('active_property_name');
      } catch (e) {}
    }
  };

  useEffect(() => {
    _getMortgageProvider();
    if (focused) {
      if (activePropertyId) {
        _getDetails(activePropertyId);
      } else {
        resetFormState();
      }
    }
  }, [focused, activePropertyId]);

  const _handleChange = (e) => {
    setState({ ...state, [e.label]: e.value });
  };

  const getAddress = (address, isAuto) => {
    let displayUrl = '';
    if (isAuto) {
      displayUrl = address?.SINGLE_LINE_ADDRESS ? address?.SINGLE_LINE_ADDRESS : '-';
    } else {
      if (address?.SUB_BUILDING) displayUrl += address?.SUB_BUILDING + ', ';
      if (address?.BUILDING_NAME) displayUrl += address?.BUILDING_NAME + ', ';
      if (address?.BUILDING_NUMBER) displayUrl += address?.BUILDING_NUMBER + ', ';
      if (address?.STREET_NAME) displayUrl += address?.STREET_NAME;
      if (address?.LOCALITY) displayUrl += address?.LOCALITY;
    }
    return displayUrl;
  };

  const _getDetails = (id) => {
    if (!id) return;
    ajax
      .get(Urls.Properties + '/' + id)
      .then(({ data }) => {
        if (data.status && data?.payload) {
          setState({
            address1: data?.payload?.address_line1 || '',
            postcode: data?.payload?.postcode || '',
            property_name: data?.payload?.name || '',
            city: data?.payload?.city || '',
            houseName: data?.payload?.house_name_num || '',
            property_worth: data?.payload?.price_worth || '',
            mortgage_provider: data?.payload?.mortgage_provider || '',
            hubita_id: data?.payload?.hubita_id || '',
            id: data?.payload?.id || id,
            country: data?.payload?.country || '',
          });
        }
      })
      .catch((error) => console.log('error fetching property details', error));
  };

  const _onAddressSelect = (address) => {
    let parsedValue = address?.value;
    if (typeof parsedValue === 'string') {
      try {
        parsedValue = JSON.parse(parsedValue);
      } catch (e) { }
    }

    const buildingName = parsedValue?.BUILDING_NAME || parsedValue?.ORGANISATION || '';
    const thoroughfare = parsedValue?.THOROUGHFARE_NAME || parsedValue?.STREET_NAME || parsedValue?.DEP_STREET || '';
    
    // Construct address1 from LINE_1/ADDRESS_1 or fallback to building + street
    let addr1 = parsedValue?.LINE_1 || parsedValue?.ADDRESS_1 || '';
    if (!addr1) {
      addr1 = `${buildingName ? buildingName + ', ' : ''}${thoroughfare}`.trim();
    }

    setState((prev) => ({
      ...prev,
      address1: addr1,
      postcode: parsedValue?.POSTCODE || parsedValue?.POSTCODE2 || parsedValue?.postcode || '',
      city: parsedValue?.POST_TOWN || parsedValue?.TOWN_OR_CITY || '',
      houseName: parsedValue?.SUB_BUILDING || parsedValue?.BUILDING_NUMBER || '',
      country: 'UK',
      property_name: buildingName || '',
    }));
  };

  const _onSave = () => {
    const targetId = activePropertyId || state?.id;
    if (state?.property_name && state?.houseName && state?.address1 && state?.postcode && state?.city && state?.country) {
      const requestBody = {
        name: state?.property_name,
        house_name_num: state?.houseName,
        address_line1: state?.address1,
        postcode: state?.postcode,
        city: state?.city,
        country: state?.country,
        price_worth: state?.property_worth?.toString(),
        mortgageProvider_id: state?.mortgage_provider,
        id: targetId || undefined,
      };
      ajax
        .post(Urls.Properties, requestBody)
        .then(({ data }) => {
          if (data.status) {
            const nextId = targetId || data?.payload?.id;
            setState((prev) => ({ ...prev, id: nextId }));
            navigation.navigate('PropertySection', { id: nextId, name: state?.property_name || routeParams?.name });
          } else {
            showErrorToast(data.message);
          }
        })
        .catch((error) => console.log('error saving property address', error));
    } else {
      showErrorToast('All fields marked * are required');
    }
  };

  const _getMortgageProvider = () => {
    ajax
      .get(Urls.MortgageProvider)
      .then(({ data }) => {
        if (data.status && data?.data) {
          const temp = data.data.map((el) => ({ label: el.name, value: el.id }));
          setMP(temp);
        }
      })
      .catch((error) => console.log('error mortgage providers', error));
  };

  const _onPostcodeSearch = () => {
    if (!state?.postcode) {
      showErrorToast('Please enter postcode value');
    } else {
      ajax
        .post(Urls.SearchAddress, { postcode: state?.postcode })
        .then(({ data }) => {
          if (data.status) {
            try {
              // if (Array.isArray(data?.data)) {
              //   let temp = [];
              //   data?.data.forEach((element) => {
              //     let label = getAddress(element);
              //     temp = [...temp, { label: label, value: JSON.stringify(element) }];
              //   });
              //   setPostcodes(temp);
              // } else {
              //   alert('Postcode not found');
              //   setPostcodes([]);
              // }

              // NOTE: new logic as per Andrew updated search postcodes API
              if(data?.data?.message) {
                // alert(data?.data?.message);
                showErrorToast(data?.data?.message);
                setPostcodes([]);
              }

              if (data?.data?.results || data?.data?.matches) {
                let temp = [];

                let items = data?.data?.matches || data?.data?.results;
                for (let key in items) {
                  let obj = items[key];
                  let label = getAddress(obj,true);
                  temp = [...temp, { label: label, value: JSON.stringify(obj) }];
                }

                setPostcodes(temp);
              }
            } catch (err) {
              setPostcodes([]);
            }
            if (data?.data?.results) {
              let temp = [];
              for (let key in data?.data?.results) {
                let obj = data?.data?.results[key];
                let label = getAddress(obj, true);
                temp.push({ label: label, value: JSON.stringify(obj) });
              }
              setPostcodes(temp);
            }
          } else {
            showErrorToast(data.message);
          }
        })
        .catch((error) => console.log('postcode search error', error));
    }
  };

  const options = postcodes.map((p) => {
    let address = JSON.parse(p.value);
    return { value: address, label: p?.label };
  });

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: BORDER_RADIUS.md,
      borderColor: COLORS.border,
      minHeight: 42,
    }),
    menu: (base) => ({
      ...base,
      zIndex: 99999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
    }),
  };

  return (
    <View style={{ flex: 1, backgroundColor: COLORS.appBg }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 24 }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header Banner Card */}
        <PropertyHeaderBanner
          icon="location-outline"
          title="Location & Address"
          subtitle="Please edit the location information of your property."
          isMobile={isMobile}
        />

        {/* 2-Column Main Layout: Form (Left) & Setup Progress Sidebar (Right) */}
        <View style={[styles.mainLayout, isMobile && styles.mainLayoutMobile]}>
          {/* Main Form Column */}
          <View style={styles.formColumn}>
            <View style={styles.formCard}>
              {/* Address Search Header Bar */}
              <View style={styles.searchBoxCard}>
                <View style={styles.searchHeaderRow}>
                  <View style={styles.searchHeaderIconWrap}>
                    <Ionicons name="search-outline" size={16} color={COLORS.primary} />
                  </View>
                  <View style={styles.searchHeaderTextWrap}>
                    <Text style={styles.searchBoxLabel}>ADDRESS Search for the property address</Text>
                    <Text style={styles.searchBoxSub}>Search by postcode to auto-fill property address details</Text>
                  </View>
                </View>

                <View style={styles.searchRow}>
                  <TextInput
                    style={styles.searchPostcodeInput}
                    placeholder="Enter Postcode (e.g. SE1 7AB)"
                    placeholderTextColor={COLORS.textMuted}
                    value={state?.postcode}
                    onChangeText={(e) => _handleChange({ value: e, label: 'postcode' })}
                    onSubmitEditing={_onPostcodeSearch}
                  />
                  <TouchableOpacity
                    style={styles.searchActionBtn}
                    onPress={_onPostcodeSearch}
                    activeOpacity={0.85}
                  >
                    <Ionicons name="search-outline" size={15} color="#FFFFFF" style={{ marginRight: 4 }} />
                    {!isMobile && <Text style={{ color: 'white', fontWeight: '500' }}>Search</Text>}
                  </TouchableOpacity>
                </View>

                {postcodes.length > 0 && (
                  <View style={{ marginTop: 14, zIndex: 99999, position: 'relative' }}>
                    <Text style={styles.dropdownLabel}>Select your address ({postcodes.length} found):</Text>
                    <Select
                      key={options.length > 0 ? options[0].value.uid || options[0].label : 'select'}
                      options={options}
                      styles={customSelectStyles}
                      onChange={_onAddressSelect}
                      isSearchable={true}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      placeholder="Select address..."
                    />
                  </View>
                )}
              </View>

              {/* Divider */}
              <View style={styles.dividerRow}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>or enter address manually below</Text>
                <View style={styles.dividerLine} />
              </View>

              {/* Manual Form Inputs */}
              <View style={styles.inputGrid}>
                {/* Dedicated Property Name Section */}
                <View style={styles.inputRowSingle}>
                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>Property Name / Title <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. Villa House"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.property_name}
                      onChangeText={(e) => _handleChange({ value: e, label: 'property_name' })}
                    />
                  </View>
                </View>

                <View style={[styles.inputRow, isMobile && styles.inputRowMobile]}>
                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>House name or number <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. 12"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.houseName}
                      onChangeText={(e) => _handleChange({ value: e, label: 'houseName' })}
                    />
                  </View>

                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>Address Line 1 <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. Compliance Lane"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.address1}
                      onChangeText={(e) => _handleChange({ value: e, label: 'address1' })}
                    />
                  </View>
                </View>

                <View style={[styles.inputRow, isMobile && styles.inputRowMobile]}>
                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>Town/city <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. London"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.city}
                      onChangeText={(e) => _handleChange({ value: e, label: 'city' })}
                    />
                  </View>

                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>Postcode <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. SE1 7AB"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.postcode}
                      onChangeText={(e) => _handleChange({ value: e, label: 'postcode' })}
                    />
                  </View>
                </View>

                <View style={styles.inputRowSingle}>
                  <View style={styles.inputFieldWrap}>
                    <Text style={styles.inputLabel}>Country <Text style={styles.requiredStar}>*</Text></Text>
                    <TextInput
                      style={styles.inputField}
                      placeholder="e.g. UK"
                      placeholderTextColor={COLORS.textMuted}
                      value={state?.country}
                      onChangeText={(e) => _handleChange({ value: e, label: 'country' })}
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right Sidebar Column (Web & Tablet) */}
          {!isMobile && (
            <SetupSidebar
              activeKey="location"
              propertyId={activePropertyId}
              propertyName={routeParams.name}
              navigation={navigation}
            />
          )}
        </View>
      </ScrollView>

      {/* Bottom Footer Actions */}
      <PropertyStickyFooter onSave={_onSave} isMobile={isMobile} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  topHeaderNav: {
    marginBottom: 16,
  },
  breadcrumbRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    flexWrap: 'wrap',
  },
  backBtnInline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 10,
  },
  backBtnText: {
    color: COLORS.primary,
    fontWeight: '600',
    fontSize: 13,
    marginLeft: 2,
  },
  breadcrumbText: {
    fontSize: 13,
    color: COLORS.textSecondary,
  },
  breadcrumbActive: {
    color: COLORS.primary,
    fontWeight: '600',
  },
  stepInfoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  stepTitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontWeight: '500',
  },
  currentStepBadge: {
    backgroundColor: COLORS.primaryLight,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: BORDER_RADIUS.full,
  },
  currentStepBadgeText: {
    color: COLORS.primary,
    fontSize: 11,
    fontWeight: '600',
  },

  // Banner Card
  bannerCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    padding: 16,
    borderWidth: 1,
    borderColor: COLORS.border,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 12,
    ...SHADOWS.sm,
  },
  bannerCardMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  bannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    width: '100%',
  },
  bannerIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: COLORS.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bannerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  bannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  bannerSubtitle: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  bannerRight: {},

  // Main Layout
  mainLayout: {
    flexDirection: 'row',
    gap: 20,
    paddingBottom: 80,
    paddingHorizontal: 20,
  },
  mainLayoutMobile: {
    flexDirection: 'column',
    paddingBottom: 80,
  },
  formColumn: {
    flex: 2,
    minWidth: 0,
  },
  sidebarColumn: {
    flex: 1,
    minWidth: 260,
  },

  // Form Card
  formCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOWS.sm,
  },
  formSectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 16,
  },

  // Search box card
  searchBoxCard: {
    backgroundColor: '#FAF9FF',
    borderRadius: BORDER_RADIUS.lg,
    padding: 18,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#E9D5FF',
    zIndex: 100,
    position: 'relative',
    ...SHADOWS.sm,
  },
  searchHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  searchHeaderIconWrap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchHeaderTextWrap: {
    flex: 1,
  },
  searchBoxLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.textPrimary,
    letterSpacing: 0.2,
  },
  searchBoxSub: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginTop: 1,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  searchPostcodeInput: {
    flex: 1,
    height: 44,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    fontSize: 14,
    color: COLORS.textPrimary,
  },
  searchActionBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 18,
    height: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...SHADOWS.sm,
  },
  dropdownLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: COLORS.textSecondary,
    marginBottom: 6,
  },

  // Divider
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: COLORS.border,
  },
  dividerText: {
    fontSize: 12,
    color: COLORS.textMuted,
    marginHorizontal: 12,
  },

  // Input Grid
  inputGrid: {
    gap: 16,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 16,
  },
  inputRowMobile: {
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  inputRowSingle: {},
  inputFieldWrap: {
    flex: 1,
    width: '100%',
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textPrimary,
    marginBottom: 6,
  },
  requiredStar: {
    color: COLORS.danger,
  },
  inputField: {
    backgroundColor: COLORS.surface,
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: BORDER_RADIUS.md,
    paddingHorizontal: 14,
    height: 44,
    fontSize: 13,
    color: COLORS.textPrimary,
    width: '100%',
  },
  charLeftText: {
    fontSize: 11,
    color: COLORS.textMuted,
    textAlign: 'right',
    marginTop: 4,
  },

  footer: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: COLORS.borderLight,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    ...SHADOWS.md,
  },
  allFieldsText: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  saveContinueBtn: {
    backgroundColor: COLORS.primary,
    borderRadius: BORDER_RADIUS.lg,
    paddingHorizontal: 22,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    ...SHADOWS.sm,
  },
  saveContinueBtnText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },

  // Sidebar Card
  sidebarCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    padding: 20,
    borderWidth: 1,
    borderColor: COLORS.border,
    ...SHADOWS.sm,
  },
  sidebarTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  sidebarSubtitle: {
    fontSize: 12,
    color: COLORS.textSecondary,
    marginTop: 4,
    marginBottom: 16,
  },
  stepList: {
    gap: 10,
  },
  stepCardItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.appBg,
    borderRadius: BORDER_RADIUS.md,
    padding: 12,
    borderWidth: 1,
    borderColor: COLORS.borderLight,
    gap: 10,
  },
  stepCardItemActive: {
    backgroundColor: COLORS.primaryLight,
    borderColor: COLORS.primary,
  },
  stepCircleIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: COLORS.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircleIconActive: {
    backgroundColor: COLORS.primary,
  },
  stepItemTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textSecondary,
  },
  stepItemTitleActive: {
    color: COLORS.primary,
  },
  stepItemSub: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginTop: 1,
  },
});

export default drawerHoc({
  Component: PropertyAddress,
  title: 'PropertyAddress',
  showBackButton: true,
  noScroll: true,
});
